/**
 * Persist follow zoom **stops** (exposure-style preference). Manual mode, position, and zoom are not persisted.
 * Migrates legacy multiplier key {@link LEGACY_STORAGE_KEY} to stops once.
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import {
  clampFollowZoomStops,
  mapLegacyFollowZoomFactorToStops,
  MAX_FOLLOW_ZOOM_STOPS,
  MIN_FOLLOW_ZOOM_STOPS,
} from './cameraZoom';

const STORAGE_KEY = 'turfmaster_followZoomStops';
/** Legacy: persisted multiplier applied to auto-fit (0.4–2.5). */
const LEGACY_STORAGE_KEY = 'turfmaster_followZoomFactor';

function migrateLegacyIfNeeded(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (window.localStorage.getItem(STORAGE_KEY) !== null) return;
    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw === null) return;
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const stops = mapLegacyFollowZoomFactorToStops(n);
    window.localStorage.setItem(STORAGE_KEY, String(stops));
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function loadFollowZoomStops(): number | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  migrateLegacyIfNeeded();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    const rounded = Math.round(n);
    if (rounded < MIN_FOLLOW_ZOOM_STOPS || rounded > MAX_FOLLOW_ZOOM_STOPS) return null;
    return rounded;
  } catch {
    return null;
  }
}

export function saveFollowZoomStops(value: number): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const clamped = clampFollowZoomStops(value);
  if (!Number.isFinite(clamped)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(clamped));
  } catch {
    // ignore
  }
}
